<template>
  <modal
    class="reflection-modal"
    :name="'create-reflection-modal'"
    width="70%"
    height="auto"
    :clickToClose="false"
    @before-open="resetForm"
  >
    <v-container class="reflection-form">
      <div class="header">
        <h2 style="color: #00A9B3; margin: 0 0 16px 0">
          {{ $t('general.reflection.create-title') }}
        </h2>
      </div>

      <div class="error" v-if="error">
        <p>{{ error }}</p>
      </div>

      <!-- Visibility checkboxes -->
      <div class="visibility-section" style="margin-bottom: 24px; padding: 16px; border: 1px solid #e0e0e0; border-radius: 4px;">
        <p style="color: #00A9B3; margin-bottom: 12px; font-weight: 500">
          {{ $t('general.reflection.visibility-title') }}
        </p>
        
        <!-- Only myself checkbox (disabled - always checked) -->
        <label class="checkbox-label" style="display: block; margin-bottom: 8px;">
          <input
            type="checkbox"
            v-model="isVisibleToSelf"
            disabled
            style="accent-color: #00A9B3"
          />
          <span style="margin-left: 8px">{{ $t('general.reflection.visibility-self') }}</span>
        </label>

        <!-- Parents checkbox -->
        <label class="checkbox-label" style="display: block; margin-bottom: 8px;">
          <input
            type="checkbox"
            v-model="isVisibleToParents"
            style="accent-color: #00A9B3"
          />
          <span style="margin-left: 8px">{{ $t('general.reflection.visibility-parents') }}</span>
        </label>

        <!-- Hospital section checkbox -->
        <label class="checkbox-label" style="display: block; margin-bottom: 8px;">
          <input
            type="checkbox"
            v-model="isVisibleToHospital"
            style="accent-color: #00A9B3"
          />
          <span style="margin-left: 8px">{{ $t('general.reflection.visibility-hospital') }}</span>
        </label>
      </div>

      <!-- Content textarea -->
      <TextAreaField
        :labelStyles="{ color: '#00A9B3', margin: '0 0 8px 0' }"
        :inputStyles="{ border: '1px solid #ddd', padding: '8px', borderRadius: '4px', width: '100%' }"
        :labelTitle="$t('general.reflection.form-content')"
        :placeholder="$t('general.reflection.form-content-placeholder')"
        v-model="content"
        rows="16"
        style="margin-bottom: 24px; width: 100%"
      />

      <!-- Action buttons -->
      <div class="button-group" style="display: flex; gap: 12px; justify-content: center; margin-top: 24px;">
        <LoadingButton v-if="loading" style="margin: 0"></LoadingButton>
        
        <div v-else style="display: flex; gap: 12px;">
          <Button
            @click.native="cancel"
            :title="$t('general.reflection.action-cancel')"
            color="#e05484"
            bgcolor="#fff"
            style="border: 2px solid #e05484"
          />
          <Button
            @click.native="saveReflection"
            :title="$t('general.reflection.action-save')"
            bgcolor="#e05484"
            color="#fff"
          />
        </div>
      </div>
    </v-container>
  </modal>
</template>

<script>
import { neoparentReflections } from "@/api";

export default {
  name: "neoparent-create-reflection",
  data() {
    return {
      content: "",
      isVisibleToSelf: true,          // Always true, read-only
      isVisibleToParents: true,       // Default true
      isVisibleToHospital: false,     // Default false
      loading: false,
      error: ""
    };
  },
  methods: {
    resetForm() {
      this.content = "";
      this.isVisibleToSelf = true;
      this.isVisibleToParents = true;
      this.isVisibleToHospital = false;
      this.error = "";
    },

    async saveReflection() {
      if (!this.content.trim()) {
        this.error = this.$t("general.reflection.error-incomplete");
        window.scrollTo(0, 0);
        return;
      }

      this.loading = true;
      this.error = "";

      try {
        const data = {
          content: this.content,
          isVisibleToSelf: this.isVisibleToSelf,
          isVisibleToParents: this.isVisibleToParents,
          isVisibleToHospital: this.isVisibleToHospital
        };

        const result = await neoparentReflections.create(
          this.$store.getters.patient.id,
          data
        );

        this.loading = false;

        // Close modal and redirect
        this.$modal.hide("create-reflection-modal");

        if (this.$store.getters.user.role === "p") {
          window.location.href = "/";
        } else if (this.$store.getters.nurse) {
          window.location.href = `/dashboard/nurse/${this.$store.getters.nurse.id}/patient/${this.$store.getters.user.patient.id}`;
        } else {
          window.location.href = `/dashboard/nurse/${this.$store.getters.user.id}/patient/${this.$store.getters.user.patient.id}`;
        }
      } catch (err) {
        this.loading = false;
        this.error = err.response?.data?.message || this.$t("general.reflection.error-save-failed");
        window.scrollTo(0, 0);
        console.error("Error saving reflection:", err);
      }
    },

    cancel() {
      this.resetForm();
      this.$modal.hide("create-reflection-modal");
      
      // Navigate back to the feed instead of staying on post creation page
      if (this.$store.getters.user.role === "p") {
        window.location.href = "/";
      } else if (this.$store.getters.nurse) {
        window.location.href = `/dashboard/nurse/${this.$store.getters.nurse.id}/patient/${this.$store.getters.user.patient.id}`;
      } else {
        window.location.href = `/dashboard/nurse/${this.$store.getters.user.id}/patient/${this.$store.getters.user.patient.id}`;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.reflection-modal {
  .reflection-form {
    padding: 24px;
    width: 100%;
    margin: 0 auto;

    .header {
      margin-bottom: 20px;
    }

    .error {
      background-color: #ffebee;
      border-left: 4px solid #f44336;
      padding: 12px 16px;
      border-radius: 4px;
      margin-bottom: 16px;

      p {
        margin: 0;
        color: #c62828;
      }
    }

    .visibility-section {
      .checkbox-label {
        cursor: pointer;
        user-select: none;

        input {
          cursor: pointer;
        }

        input:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }
    }

    .button-group {
      margin-top: 20px;
    }
  }
}

@media (max-width: 700px) {
  .reflection-modal {
    .reflection-form {
      max-width: 100%;
    }
  }
}
</style>
